import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NSNavbar from '../../components/NSNavbar';
import NSFooter from '../../components/NSFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath } from '../../utils/brandRouting';
import { SUPPORT_EMAIL, products } from '../../data/siteContent';

const NUTRISCAN = products.find((product) => product.id === 'nutriscan');
const AVAILABILITY = 'Available on Google Play';

/* Apply data-brand="nutriscan" to body so global NS theme variables kick in */
function useNSTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'nutriscan');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

/**
 * NutriScan product page. The public claim set is deliberately small: the
 * founder-approved catalogue description and the verified Google Play
 * destination, both from data/siteContent. No ratings, user counts, scan
 * examples, database sizes, speed figures, or platform claims beyond Google
 * Play — see the T04 content brief.
 */
export default function NutriScan() {
  useNSTheme();
  useDocumentMeta(routeMetaFor('/apps/nutriscan'));

  return (
    <PageWrap testId="nutriscan-page">
      <NSNavbar />

      {/* HERO */}
      <section className="ns-hero" data-testid="ns-hero" style={{ minHeight: 'auto', paddingTop: 'calc(var(--nav-h) + 96px)', paddingBottom: 96 }}>
        <div className="ns-hero-leaf-1"></div>
        <div className="ns-hero-leaf-2"></div>
        <div className="ns-hero-grid-pattern"></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow reveal" style={{ color: '#86EFAC' }}>{AVAILABILITY}</div>
          <h1 className="reveal d1" style={{ color: '#fff', marginBottom: '18px' }}>{NUTRISCAN.name}</h1>
          <p className="lead reveal d2" style={{ color: 'rgba(255,255,255,0.78)', marginBottom: '32px', maxWidth: '560px' }}>
            {NUTRISCAN.description}
          </p>
          <div className="btn-group reveal d3">
            <a
              href={NUTRISCAN.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ns-store-btn"
              data-testid="ns-playstore-btn"
            >
              <i className="fab fa-google-play"></i>
              <div><span>Get it on</span><strong>{NUTRISCAN.storeLabel}</strong></div>
            </a>
          </div>
        </div>
      </section>

      {/* LEGAL, FAQ AND SUPPORT ROUTES */}
      <section className="ns-section-white" style={{ paddingBottom: 64 }} data-testid="ns-resources">
        <div className="wrap">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
            <div>
              <div className="eyebrow reveal">Resources</div>
              <h2 className="reveal d1">Legal &amp; support</h2>
            </div>
            <Link to={brandPath('nutriscan', '/faq')} className="btn btn-ns-outline reveal" data-testid="ns-faq-cta">All NutriScan FAQs →</Link>
          </div>
          <div className="grid-3">
            <Link to={brandPath('nutriscan', '/privacy')} className="ns-legal-card reveal" data-testid="ns-privacy-link"><i className="fas fa-shield-halved"></i><div><strong style={{ color: 'var(--ns-dark)' }}>Privacy Policy</strong><div style={{ fontSize: '0.82rem', color: 'var(--ns-muted)' }}>What we collect &amp; why</div></div></Link>
            <Link to={brandPath('nutriscan', '/terms')} className="ns-legal-card reveal d1" data-testid="ns-terms-link"><i className="fas fa-file-contract"></i><div><strong style={{ color: 'var(--ns-dark)' }}>Terms of Service</strong><div style={{ fontSize: '0.82rem', color: 'var(--ns-muted)' }}>How NutriScan works for you</div></div></Link>
            <Link to={brandPath('nutriscan', '/faq')} className="ns-legal-card reveal d2" data-testid="ns-faq-link"><i className="fas fa-circle-question"></i><div><strong style={{ color: 'var(--ns-dark)' }}>FAQ</strong><div style={{ fontSize: '0.82rem', color: 'var(--ns-muted)' }}>Common questions answered</div></div></Link>
            <Link to={brandPath('nutriscan', '/account-deletion')} className="ns-legal-card reveal d3" data-testid="ns-deletion-link"><i className="fas fa-user-xmark"></i><div><strong style={{ color: 'var(--ns-dark)' }}>Account Deletion</strong><div style={{ fontSize: '0.82rem', color: 'var(--ns-muted)' }}>How to delete your account</div></div></Link>
          </div>
          <p className="reveal d3" style={{ marginTop: 28, fontSize: '0.9rem', color: 'var(--ns-muted)' }}>
            Questions about NutriScan? Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--ns-green-text)', fontWeight: 600 }} data-testid="ns-support-email">{SUPPORT_EMAIL}</a>.
          </p>
        </div>
      </section>

      <NSFooter />
    </PageWrap>
  );
}
